import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { ChevronDown, ChevronUp, Lock, Plus, Search, Trash2 } from 'lucide-react'
import type { CollectionTarget } from '../../../types/collection-targets'
import { UploadGateStatus } from '../UploadGateStatus'
import type { SortDir, SortField } from './use-collection-targets-table'
import { TARGET_STATUSES } from './collection-targets-table.constants'

const COLSPAN_LOCKED = 7
const COLSPAN_UNLOCKED = 8

export function LoadingSpinner(): ReactElement {
  return (
    <div className="items-centre justify-centre flex p-8" role="status">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-fuchsia-600 border-t-transparent" />
    </div>
  )
}

interface ActionBarProps {
  isLocked: boolean
  selectedCount: number
  removingTargets: boolean
  onAddTargets?: (() => void) | undefined
  onRemoveSelected?: (() => void) | undefined
  t: TFunction<'cases'>
}

export function ActionBar(props: ActionBarProps): ReactElement {
  const { isLocked, selectedCount, removingTargets, onAddTargets, onRemoveSelected, t } = props
  return (
    <div className="items-centre flex justify-between">
      <div className="items-centre flex gap-2">
        {isLocked && (
          <span className="items-centre inline-flex gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
            <Lock className="h-3 w-3" />
            {t('collections.locked')}
          </span>
        )}
      </div>
      <div className="items-centre flex gap-2">
        {!isLocked && onAddTargets && (
          <button
            onClick={onAddTargets}
            className="items-centre inline-flex gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-fuchsia-700 focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 focus:outline-none"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('collections.addTargets')}
          </button>
        )}
        {!isLocked && onRemoveSelected && selectedCount > 0 && (
          <button
            onClick={onRemoveSelected}
            disabled={removingTargets}
            className="items-centre inline-flex gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('collections.removeTargets', { count: selectedCount })}
          </button>
        )}
      </div>
    </div>
  )
}

function statusLabelTitleCase(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

interface FiltersProps {
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  statusFilter: string
  setStatusFilter: React.Dispatch<React.SetStateAction<string>>
  t: TFunction<'cases'>
}

export function Filters({
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  t,
}: FiltersProps): ReactElement {
  return (
    <div className="items-centre flex gap-3">
      <div className="relative flex-1">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
          }}
          placeholder={t('collections.targetMonitoring.searchHostname')}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        />
      </div>
      <select
        value={statusFilter}
        onChange={(e) => {
          setStatusFilter(e.target.value)
        }}
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
      >
        <option value="">{t('collections.targetMonitoring.allStatuses')}</option>
        {TARGET_STATUSES.map((status) => (
          <option key={status} value={status}>
            {statusLabelTitleCase(status)}
          </option>
        ))}
      </select>
    </div>
  )
}

interface ResultCountProps {
  filteredCount: number
  totalCount: number
  t: TFunction<'cases'>
}

export function ResultCount({ filteredCount, totalCount, t }: ResultCountProps): ReactElement {
  return (
    <p className="text-xs text-slate-500 dark:text-slate-400">
      {t('collections.targetMonitoring.showingTargets', {
        count: filteredCount,
        total: totalCount,
      })}
    </p>
  )
}

interface SortHeaderProps {
  label: string
  field: SortField
  current: SortField
  dir: SortDir
  onSort: (f: SortField) => void
}

function SortHeader({ label, field, current, dir, onSort }: SortHeaderProps): ReactElement {
  return (
    <th className="px-3 py-2">
      <button
        onClick={() => {
          onSort(field)
        }}
        className="items-centre flex gap-1 font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        {label}
        {current === field &&
          (dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    </th>
  )
}

const STATUS_BADGE_COLOURS: Record<string, { bg: string; text: string; dot: string }> = {
  pending: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-600 dark:text-slate-400',
    dot: 'bg-slate-400',
  },
  in_progress: {
    bg: 'bg-blue-100 dark:bg-blue-950/50',
    text: 'text-blue-700 dark:text-blue-400',
    dot: 'bg-blue-500',
  },
  collecting: {
    bg: 'bg-blue-100 dark:bg-blue-950/50',
    text: 'text-blue-700 dark:text-blue-400',
    dot: 'bg-blue-500 animate-pulse',
  },
  collected: {
    bg: 'bg-green-100 dark:bg-green-950/50',
    text: 'text-green-700 dark:text-green-400',
    dot: 'bg-green-500',
  },
  partial: {
    bg: 'bg-orange-100 dark:bg-orange-950/50',
    text: 'text-orange-700 dark:text-orange-400',
    dot: 'bg-orange-500',
  },
  failed: {
    bg: 'bg-red-100 dark:bg-red-950/50',
    text: 'text-red-700 dark:text-red-400',
    dot: 'bg-red-500',
  },
  client_halted: {
    bg: 'bg-orange-100 dark:bg-orange-950/50',
    text: 'text-orange-700 dark:text-orange-400',
    dot: 'bg-orange-500',
  },
  cancelled: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-500 dark:text-slate-400',
    dot: 'bg-slate-300',
  },
  ingested: {
    bg: 'bg-green-100 dark:bg-green-950/50',
    text: 'text-green-700 dark:text-green-400',
    dot: 'bg-green-500',
  },
}

const FALLBACK_COLOURS = {
  bg: 'bg-slate-100 dark:bg-slate-800',
  text: 'text-slate-600 dark:text-slate-400',
  dot: 'bg-slate-400',
}

function statusLabel(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

function TargetStatusBadge({ status }: { status: string }): ReactElement {
  const colours = STATUS_BADGE_COLOURS[status] ?? FALLBACK_COLOURS
  return (
    <span
      className={`items-centre inline-flex gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${colours.bg} ${colours.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colours.dot}`} />
      {statusLabel(status)}
    </span>
  )
}

interface TableHeaderRowProps {
  isLocked: boolean
  filteredCount: number
  selectedSize: number
  toggleAll: () => void
  sortField: SortField
  sortDir: SortDir
  toggleSort: (field: SortField) => void
  t: TFunction<'cases'>
}

function TableHeaderRow(props: TableHeaderRowProps): ReactElement {
  const { isLocked, filteredCount, selectedSize, toggleAll, sortField, sortDir, toggleSort, t } =
    props
  return (
    <tr className="border-b border-slate-100 bg-slate-50 text-left dark:border-slate-800 dark:bg-slate-800/50">
      {!isLocked && (
        <th className="w-10 px-3 py-2">
          <input
            type="checkbox"
            checked={filteredCount > 0 && selectedSize === filteredCount}
            onChange={toggleAll}
            className="rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500"
            aria-label={t('collections.targetMonitoring.selectAll')}
          />
        </th>
      )}
      <SortHeader
        label={t('collections.targetMonitoring.hostname')}
        field="hostname"
        current={sortField}
        dir={sortDir}
        onSort={toggleSort}
      />
      <SortHeader
        label={t('collections.targetMonitoring.status')}
        field="status"
        current={sortField}
        dir={sortDir}
        onSort={toggleSort}
      />
      <SortHeader
        label={t('collections.targetMonitoring.evidenceId')}
        field="evidenceId"
        current={sortField}
        dir={sortDir}
        onSort={toggleSort}
      />
      <SortHeader
        label={t('collections.targetMonitoring.priority')}
        field="priority"
        current={sortField}
        dir={sortDir}
        onSort={toggleSort}
      />
      <th className="px-3 py-2 font-medium text-slate-500 dark:text-slate-400">
        {t('collections.targetMonitoring.os')}
      </th>
      <th className="px-3 py-2 font-medium text-slate-500 dark:text-slate-400">
        {t('collections.targetMonitoring.assignedTo')}
      </th>
      <th className="px-3 py-2 font-medium text-slate-500 dark:text-slate-400">
        {t('collections.targetMonitoring.upload')}
      </th>
    </tr>
  )
}

interface EmptyRowProps {
  isLocked: boolean
  hasFilters: boolean
  t: TFunction<'cases'>
}

function EmptyRow({ isLocked, hasFilters, t }: EmptyRowProps): ReactElement {
  return (
    <tr>
      <td
        colSpan={isLocked ? COLSPAN_LOCKED : COLSPAN_UNLOCKED}
        className="text-centre px-3 py-8 text-sm text-slate-400 italic dark:text-slate-500"
      >
        {hasFilters
          ? t('collections.targetMonitoring.noTargetsMatch')
          : t('collections.emptyCollection')}
      </td>
    </tr>
  )
}

interface TargetRowProps {
  target: CollectionTarget
  isLocked: boolean
  isSelected: boolean
  toggleOne: (id: string) => void
  t: TFunction<'cases'>
}

function TargetRow({ target, isLocked, isSelected, toggleOne, t }: TargetRowProps): ReactElement {
  return (
    <tr
      className={`border-b border-slate-50 transition dark:border-slate-800 ${
        isSelected
          ? 'bg-fuchsia-50/50 dark:bg-fuchsia-950/20'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }`}
    >
      {!isLocked && (
        <td className="px-3 py-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {
              toggleOne(target.id)
            }}
            className="rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500"
          />
        </td>
      )}
      <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-200">
        {target.endpointHostname}
      </td>
      <td className="px-3 py-2">
        <TargetStatusBadge status={target.targetStatus} />
        {target.targetStatus === 'client_halted' && target.notes && (
          <p className="mt-1 text-xs text-orange-600 dark:text-orange-400">{target.notes}</p>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
        {target.evidenceId ?? '—'}
      </td>
      <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{target.priority}</td>
      <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{target.endpointOs ?? '—'}</td>
      <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{target.assignedTo ?? '—'}</td>
      <td className="px-3 py-2">
        <UploadGateStatus target={target} t={t} />
      </td>
    </tr>
  )
}

export interface TargetsTableProps {
  isLocked: boolean
  filtered: CollectionTarget[]
  selectedIds: Set<string>
  toggleAll: () => void
  toggleOne: (id: string) => void
  sortField: SortField
  sortDir: SortDir
  toggleSort: (field: SortField) => void
  hasFilters: boolean
  t: TFunction<'cases'>
}

export function TargetsTable(props: TargetsTableProps): ReactElement {
  const {
    isLocked,
    filtered,
    selectedIds,
    toggleAll,
    toggleOne,
    sortField,
    sortDir,
    toggleSort,
    hasFilters,
    t,
  } = props
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <table className="w-full text-sm">
        <thead>
          <TableHeaderRow
            isLocked={isLocked}
            filteredCount={filtered.length}
            selectedSize={selectedIds.size}
            toggleAll={toggleAll}
            sortField={sortField}
            sortDir={sortDir}
            toggleSort={toggleSort}
            t={t}
          />
        </thead>
        <tbody>
          {filtered.length === 0 && <EmptyRow isLocked={isLocked} hasFilters={hasFilters} t={t} />}
          {filtered.map((tgt) => (
            <TargetRow
              key={tgt.id}
              target={tgt}
              isLocked={isLocked}
              isSelected={selectedIds.has(tgt.id)}
              toggleOne={toggleOne}
              t={t}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
